import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import { Link } from 'dibs-elements/exports/Link';
import { NavItem } from './NavItem';
import { trackNavInteraction } from '../../helpers/tracking';
import { NavItemVATReportLink } from './NavItemVATReportLink';
import { VAT_REPORT_MODAL_HASH } from '../VatReportModal/VatReportModal';
import { useNavContext } from '../../NavContext';

import { type NavExpandingAreaItem_seller$key } from './__generated__/NavExpandingAreaItem_seller.graphql';
import { type NavExpandingAreaItem_navItem$key } from './__generated__/NavExpandingAreaItem_navItem.graphql';
import { type NavExpandingAreaItem_notifications$key } from './__generated__/NavExpandingAreaItem_notifications.graphql';
import { type NavExpandingAreaItem_viewer$key } from './__generated__/NavExpandingAreaItem_viewer.graphql';

type Props = {
    activeLink: string;
    seller?: NavExpandingAreaItem_seller$key | null;
    navItem: NavExpandingAreaItem_navItem$key;
    notifications?: NavExpandingAreaItem_notifications$key | null;
    viewer?: NavExpandingAreaItem_viewer$key | null;
};

const sellerFragment = graphql`
    fragment NavExpandingAreaItem_seller on Seller {
        ...NavItem_seller
        ...NavItemVATReportLink_seller
    }
`;

const navItemFragment = graphql`
    fragment NavExpandingAreaItem_navItem on NavBarElement {
        ...NavItem_navItem
        ...NavItemVATReportLink_navItem
        newWindow
        eventName
        interactionType
        href
        title
        dataTn
    }
`;

const notificationsFragment = graphql`
    fragment NavExpandingAreaItem_notifications on DealerNavBarBadges {
        ...NavItem_notifications
        ...NavItemVATReportLink_notifications
    }
`;

const viewerFragment = graphql`
    fragment NavExpandingAreaItem_viewer on Viewer {
        ...NavItem_viewer
    }
`;

export const NavExpandingAreaItem: FC<Props> = ({
    activeLink,
    seller: sellerRef,
    navItem: navItemRef,
    notifications: notificationsRef,
    viewer: viewerRef,
}) => {
    const seller = useFragment(sellerFragment, sellerRef);
    const navItem = useFragment(navItemFragment, navItemRef);
    const notifications = useFragment(notificationsFragment, notificationsRef);
    const viewer = useFragment(viewerFragment, viewerRef);
    const { setStatus, setExpandedArea, status } = useNavContext();

    const { title, href, eventName, interactionType, newWindow, dataTn } = navItem;

    const isActiveItem = href === activeLink;

    return href === VAT_REPORT_MODAL_HASH ? (
        <NavItemVATReportLink
            seller={seller}
            navItem={navItem}
            notifications={notifications}
            isActive={isActiveItem}
        />
    ) : (
        <Link
            key={title}
            href={href || ''}
            dataTn={dataTn || undefined}
            target={newWindow ? '_blank' : undefined}
            onClick={() => {
                if (status === 'overlay') {
                    setExpandedArea(null);
                    setStatus('collapsed');
                }
                trackNavInteraction({
                    action: eventName,
                    label: eventName,
                    interactionType,
                });
            }}
        >
            <NavItem
                seller={seller}
                navItem={navItem}
                notifications={notifications}
                isActive={isActiveItem}
                isExpandingAreaItem
                viewer={viewer}
            />
        </Link>
    );
};
